// src/lib/components/organism/Node/AtlasPerspectiveSelector.tsx

import React from 'react';
import PerspectiveSelector from '../../PerspectiveSelector/PerspectiveSelector';
import { Sphere } from 'types/global';
import type { FilterExpression } from 'types/filters';
import { useSpherePermission } from '../../../../hooks/useSpherePermission';
import './AtlasPerspectiveSelector.scss';

interface AtlasPerspectiveSelectorProps {
  perspectiveSystemName: string;
  sphere: Sphere;
  parentNode?: Sphere;
  organizationId: string;
  isSelected: boolean;
  activeSection: string;
  activeStyles: React.CSSProperties;
  onAssigneeSelected?: (
    assignee: string | null,
    sphereId: string,
    perspectiveSystemName: string
  ) => void;
  onAssigneesSelected?: (
    assignees: string[] | null,
    sphereId: string,
    perspectiveSystemName: string
  ) => void;
  onDone?: (
    sphereId: string,
    perspectiveSystemName: string,
    assignees: string[]
  ) => void;
  showLabel?: boolean; // Optional prop to toggle the label
  className?: string;
  style?: React.CSSProperties;
  cardinality?: 'single' | 'multiple';
  contextFilters?: FilterExpression[] | null;
  /**
   * The column's `assignable` setting (`moleculeProps.assignable`). Defaults to `true`
   * for stored views authored before this flag existed. This is a UI convenience only —
   * `assignSubjectToPerspective.ts:264` re-checks `edit` server-side on every mutation.
   */
  assignable?: boolean;
  /** `moleculeProps.display` — single-cardinality read state: avatar, name, or both. */
  display?: 'avatar+name' | 'avatar' | 'name';
  /**
   * `moleculeProps.maxAvatars` — caps the avatar stack for multi-cardinality columns
   * (`MultiSelectPerspectiveSelector` → `PerspectiveAssigneesDisplayAtom`). No effect
   * on a single-cardinality column, which only ever shows one assignee.
   */
  maxAvatars?: number;
  /** `moleculeProps.placeholder` — shown when a single-cardinality column is unassigned. */
  placeholder?: string;
}

const AtlasPerspectiveSelector: React.FC<AtlasPerspectiveSelectorProps> = ({
  perspectiveSystemName,
  sphere,
  organizationId,
  isSelected: _isSelected,
  activeStyles: _activeStyles,
  parentNode,
  onAssigneeSelected,
  onAssigneesSelected,
  onDone,
  showLabel = false,
  className = '',
  style = {},
  cardinality = 'single',
  contextFilters,
  assignable = true,
  display,
  maxAvatars,
  placeholder,
}) => {

  const isMulti = cardinality === 'multiple';

  // "Assign-from-table is an edit" (spec Security Considerations) — the column's own
  // `assignable` toggle can only ever narrow the control, never grant a right the caller
  // doesn't have. A pending permission check is treated as denied, not allowed, so the
  // control never flashes open before closing back down once the check resolves.
  const { allowed: canEditSphere, loading: permissionLoading } = useSpherePermission(sphere?.id, 'edit');
  const canAssign = assignable && canEditSphere && !permissionLoading;

  return (
    <div className={`atlas-perspective-selector  ${className}`} style={style}>
      {/* Optional Label */}
      {showLabel && (
        <label
          htmlFor={`atlas-perspective-select-${perspectiveSystemName}`}
          className="atlas-perspective-label"
        >
          {sphere.properties.name}
        </label>
      )}

      {/* PerspectiveSelector Component */}
      <PerspectiveSelector
        perspectiveSystemName={perspectiveSystemName}
        sphere={sphere}
        organizationId={organizationId}
        onAssigneeSelected={onAssigneeSelected}
        onAssigneesSelected={onAssigneesSelected}
        onDone={onDone}
        showLabel={showLabel} // Pass through the showLabel prop
        isAtlas={true} // Add a new prop to the PerspectiveSelector component
        nearbySphereId={parentNode?.id}
        multi={isMulti}
        contextFilters={contextFilters}
        assignable={canAssign}
        display={display}
        maxAvatars={maxAvatars}
        placeholder={placeholder}
      />
    </div>
  );
};

export default AtlasPerspectiveSelector;
